import { useState, useContext } from 'react';
import { supabase } from '../supabaseClient';
import { AuthContext } from '../context/AuthContext';
import {
  DOCUMENT_STATUSES,
  STORAGE,
  MESSAGES
} from '../config/constants';
import { usePermissions } from './usePermissions'; // Assuming usePermissions is in the hooks folder

// Helper function (could be moved to utils if used elsewhere)
const sanitizeFilename = (filename) => {
  // Remove special characters and replace spaces with underscores
  return filename
    .replace(/[^\w\d.-]/g, '_')
    .replace(/_{2,}/g, '_'); // Replace multiple consecutive underscores with a single one
};

export const useFileUpload = (selectedProjectId, afterUploadCallback, showErrorCallback) => {
  const { user } = useContext(AuthContext);
  const { hasPermission } = usePermissions();
  const [uploadProgress, setUploadProgress] = useState({
    visible: false,
    totalFiles: 0,
    completedFiles: 0,
    currentFileProgress: 0,
    currentFileName: '',
    error: false,
    errorMessage: ''
  });

  const uploadFiles = async (files) => {
    if (!hasPermission('upload_document')) {
      showErrorCallback('Je hebt geen rechten om documenten te uploaden');
      return;
    }

    if (!selectedProjectId) {
      showErrorCallback('Selecteer eerst een project voordat je documenten upload');
      return;
    }

    if (!files || files.length === 0) { 
      showErrorCallback(MESSAGES.ERRORS.NO_FILES);
      return;
    }

    // Validate file types
    const invalidFiles = Array.from(files).filter(file => !STORAGE.ALLOWED_TYPES.includes(file.type));
    if (invalidFiles.length > 0) {
      showErrorCallback(MESSAGES.ERRORS.FILE_TYPE);
      return;
    }

    // Check file sizes
    const oversizedFiles = Array.from(files).filter(file => file.size > STORAGE.MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      showErrorCallback(MESSAGES.ERRORS.FILE_SIZE);
      return;
    }

    // Initialize upload progress state
    setUploadProgress({
      visible: true,
      totalFiles: files.length,
      completedFiles: 0,
      currentFileProgress: 0,
      currentFileName: files[0]?.name || '',
      error: false,
      errorMessage: ''
    });

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Update current file info
        setUploadProgress(prev => ({
          ...prev,
          currentFileName: file.name,
          currentFileProgress: 0
        }));
        
        // Create sanitized filename for storage while keeping original name
        const timestamp = Date.now();
        const originalName = file.name;
        const sanitizedName = sanitizeFilename(file.name);
        const storageName = `${timestamp}-${sanitizedName}`;
        const filePath = `${selectedProjectId}/${storageName}`;

        // Determine file type and bucket
        const isXmlFile = file.type === 'application/xml' || file.type === 'text/xml';
        const bucketName = isXmlFile ? 'xml-import' : STORAGE.BUCKET_NAME;
        const { error: uploadError } = await new Promise((resolve) => {
          const uploadTask = supabase.storage
            .from(bucketName)
            .upload(filePath, file, {
              contentType: file.type,
              cacheControl: '3600'
            });

          // Simulate progress updates since Supabase doesn't provide upload progress
          const totalSize = file.size;
          let simulatedLoaded = 0;
          const progressInterval = setInterval(() => {
            if (simulatedLoaded < totalSize) {
              // Simulate progress as a percentage of file size
              simulatedLoaded += totalSize / 20; // 5% increments
              if (simulatedLoaded > totalSize) simulatedLoaded = totalSize;
              
              const percent = Math.min(95, Math.round((simulatedLoaded / totalSize) * 100));
              setUploadProgress(prev => ({
                ...prev,
                currentFileProgress: percent
              }));
            }
          }, 200);

          uploadTask.then(result => {
            clearInterval(progressInterval);
            // Check for upload result error here if needed
            resolve(result);
          }).catch(err => {
            clearInterval(progressInterval);
            resolve({ error: err }); // Ensure the promise resolves even on error
          });
        });

        if (uploadError) throw uploadError;

        // Set progress to 100% when file is completely uploaded
        setUploadProgress(prev => ({
          ...prev,
          currentFileProgress: 100
        }));

        // Handle different file types
        if (isXmlFile) {
          // For XML files, send to import function (no database record creation)
          const { data: importData, error: importError } = await supabase.functions.invoke('import-sikb-xml', {
            body: {
              storagePath: filePath
            }
          });

          if (importError) {
            console.error('XML import error:', importError);
            throw new Error(`XML import failed: ${importError.message}`);
          }
          
          console.log('XML import success:', importData);
        } else {
          // For PDF files, create document record as before
          const { error: dbError } = await supabase
            .from('documents')
            .insert([{
              project_id: selectedProjectId,
              name: originalName, // Store original filename
              storage_path: filePath,
              file_type: file.type,
              size: file.size,
              status: DOCUMENT_STATUSES.INTAKE,
              uploaded_by: user.id,
              received_at: new Date().toISOString()
            }]);

          if (dbError) throw dbError;
        }

        // Update completed files count
        setUploadProgress(prev => ({
          ...prev,
          completedFiles: i + 1
        }));
      }

      // Final update - all files completed
      setUploadProgress(prev => ({
        ...prev,
        currentFileName: 'Afgerond',
        currentFileProgress: 100
      }));

      // Call the afterUploadCallback (e.g., fetchDocuments)
      if (afterUploadCallback) {
          await afterUploadCallback(selectedProjectId);
      }
      
      // Hide progress indicator after a short delay
      setTimeout(() => {
        setUploadProgress(prev => ({ ...prev, visible: false }));
      }, 2000);
    } catch (error) {
      console.error('Upload error:', error);
      showErrorCallback(MESSAGES.ERRORS.UPLOAD_FAILED);
      
      // Show error in progress indicator
      setUploadProgress(prev => ({
        ...prev,
        error: true,
        errorMessage: MESSAGES.ERRORS.UPLOAD_FAILED
      }));
      
      // Hide progress indicator after error
      setTimeout(() => {
        setUploadProgress(prev => ({ ...prev, visible: false }));
      }, 5000);
    }
  };

  return { uploadProgress, uploadFiles };
}; 